package com.tovtalk.data.repository

import com.tovtalk.data.remote.ApiClient
import com.tovtalk.data.remote.ApiEndpoints
import com.tovtalk.data.remote.dto.CategoryDto
import com.tovtalk.data.remote.dto.DocSlugsDto
import com.tovtalk.data.remote.dto.KosherLevelDto
import com.tovtalk.data.remote.dto.PageContentDto
import com.tovtalk.data.remote.dto.V2PaginatedData
import com.tovtalk.data.remote.dto.V2Response
import com.tovtalk.domain.entity.AppConfig
import com.tovtalk.domain.entity.Category
import com.tovtalk.domain.entity.ContactPhoneItem
import com.tovtalk.domain.entity.KosherLevel
import com.tovtalk.domain.entity.DomainResult
import com.tovtalk.domain.repository.AppRepository
import com.tovtalk.utils.logger.Logger
import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.client.request.parameter
import kotlin.time.Clock
import kotlinx.coroutines.withTimeout

class AppRepositoryImpl(
    private val apiClient: ApiClient
) : AppRepository {

    companion object {
        private const val CONFIG_TTL_MS = 3_600_000L // 1 hour
        private const val DOC_SLUGS_TTL_MS = 300_000L // 5 minutes
        private const val DOC_SLUGS_TIMEOUT_MS = 2_000L
    }

    private var cachedAppConfig: AppConfig? = null
    private var appConfigTimestamp = 0L
    private var cachedKosherLevels: List<KosherLevel>? = null
    private var kosherLevelsTimestamp = 0L
    private var cachedDocSlugs: Set<String>? = null
    private var docSlugsTimestamp = 0L

    override suspend fun getAppConfig(): DomainResult<AppConfig> {
        val now = Clock.System.now().toEpochMilliseconds()
        cachedAppConfig?.let { cached ->
            if (now - appConfigTimestamp < CONFIG_TTL_MS) return DomainResult.Success(cached)
        }
        return try {
            val response = apiClient.httpClient.get(ApiEndpoints.APP_CONFIG)
            val body = response.body<AppConfig>()
            cachedAppConfig = body
            appConfigTimestamp = now
            DomainResult.Success(body)
        } catch (e: Exception) {
            Logger.e("getAppConfig failed: ${e.message}", "AppRepositoryImpl", e)
            // Return stale cache on network error
            cachedAppConfig?.let { return DomainResult.Success(it) }
            DomainResult.Error(e.message ?: "Unknown error")
        }
    }

    override suspend fun getContactPhones(): DomainResult<List<ContactPhoneItem>> {
        return try {
            val response = apiClient.httpClient.get(ApiEndpoints.CONTACT_PHONES)
            val result = response.body<V2Response<V2PaginatedData<ContactPhoneItem>>>()
            val items = result.data?.items
            if (items != null) {
                DomainResult.Success(items)
            } else {
                val errorMsg = result.errors?.firstOrNull()?.message ?: "Failed to load contact phones"
                DomainResult.Error(errorMsg)
            }
        } catch (e: Exception) {
            Logger.e("getContactPhones failed: ${e.message}", "AppRepositoryImpl", e)
            DomainResult.Error(e.message ?: "Unknown error")
        }
    }

    override suspend fun getKosherLevels(hadranOnly: Boolean): DomainResult<List<KosherLevel>> {
        val now = Clock.System.now().toEpochMilliseconds()
        if (!hadranOnly) {
            cachedKosherLevels?.let { cached ->
                if (now - kosherLevelsTimestamp < CONFIG_TTL_MS) return DomainResult.Success(cached)
            }
        }
        return try {
            val response = apiClient.httpClient.get(ApiEndpoints.KOSHER_LEVELS) {
                if (hadranOnly) {
                    parameter("hadran_only", "true")
                }
            }

            val result = response.body<V2Response<V2PaginatedData<KosherLevelDto>>>()

            val items = result.data?.items
            if (items != null) {
                val levels = items.map { dto ->
                    KosherLevel(
                        id = dto.id,
                        order = dto.order,
                        allowedOnHadran = dto.allowedOnHadran,
                        requiresGenderVerification = dto.requiresGenderVerification,
                        nameKey = dto.nameKey,
                        descriptionKey = dto.descriptionKey,
                        nameHe = dto.nameHe,
                        nameEn = dto.nameEn,
                        allowTextComments = dto.allowTextComments,
                        allowEmojiReactions = dto.allowEmojiReactions,
                    )
                }
                if (!hadranOnly) {
                    cachedKosherLevels = levels
                    kosherLevelsTimestamp = now
                }
                DomainResult.Success(levels)
            } else {
                val errorMsg = result.errors?.firstOrNull()?.message ?: "Failed to load kosher levels"
                DomainResult.Error(errorMsg)
            }
        } catch (e: Exception) {
            Logger.e("getKosherLevels failed: ${e.message}", "AppRepositoryImpl", e)
            if (!hadranOnly) cachedKosherLevels?.let { return DomainResult.Success(it) }
            DomainResult.Error(e.message ?: "Unknown error")
        }
    }

    override suspend fun getCategories(): DomainResult<List<Category>> {
        return try {
            val response = apiClient.httpClient.get(ApiEndpoints.CATEGORIES)
            val result = response.body<V2Response<V2PaginatedData<CategoryDto>>>()
            val items = result.data?.items
            if (items != null) {
                val categories = items.map { dto ->
                    Category(id = dto.id, name = dto.name, sortOrder = dto.sortOrder)
                }
                DomainResult.Success(categories)
            } else {
                val errorMsg = result.errors?.firstOrNull()?.message ?: "Failed to load categories"
                DomainResult.Error(errorMsg)
            }
        } catch (e: Exception) {
            Logger.e("getCategories failed: ${e.message}", "AppRepositoryImpl", e)
            DomainResult.Error(e.message ?: "Unknown error")
        }
    }

    override suspend fun getPageContent(slug: String, lang: String): DomainResult<String> {
        return try {
            val response = apiClient.httpClient.get("${ApiEndpoints.PAGES}$slug") {
                parameter("lang", lang)
            }
            val body = response.body<V2Response<PageContentDto>>()
            DomainResult.Success(body.data?.content.orEmpty())
        } catch (e: Exception) {
            Logger.e("getPageContent($slug) failed: ${e.message}", "AppRepositoryImpl", e)
            DomainResult.Error(e.message ?: "Unknown error")
        }
    }

    override suspend fun getKnownStaticPageSlugs(): DomainResult<Set<String>> {
        val now = Clock.System.now().toEpochMilliseconds()
        cachedDocSlugs?.let { cached ->
            if (now - docSlugsTimestamp < DOC_SLUGS_TTL_MS) return DomainResult.Success(cached)
        }
        return try {
            withTimeout(DOC_SLUGS_TIMEOUT_MS) {
                val response = apiClient.httpClient.get(ApiEndpoints.DOC_SLUGS)
                val body = response.body<V2Response<DocSlugsDto>>()
                val slugs = body.data?.slugs.orEmpty().map { it.lowercase() }.toSet()
                cachedDocSlugs = slugs
                docSlugsTimestamp = now
                DomainResult.Success(slugs)
            }
        } catch (e: Exception) {
            Logger.e("getKnownStaticPageSlugs failed: ${e.message}", "AppRepositoryImpl", e)
            cachedDocSlugs?.let { return DomainResult.Success(it) }
            DomainResult.Error(e.message ?: "Unknown error")
        }
    }
}
